// Turkey Hunt scene engine: a small Canvas 2D renderer at 480x270 with
// layered scenery, three numbered turkeys with distinct behaviours, effects
// and pixel-accurate hit testing that respects every occluder.

import { GEO, TK_AX, TK_AY, TK_BADGE, TK_H, TK_W, getScenery, getTurkeyFrames, type Scenery, type TurkeyFrames, type TurkeySprite } from "./art";
import { H, W, digit, ellipse, px, rect } from "./pixel";

export type Behaviour = "fly" | "run" | "barn" | "tree" | "hay" | "fence";
type TState = "active" | "spin" | "dizzy" | "exit" | "gone";

interface Point { x: number; y: number }

interface Turkey {
  n: number;
  b: Behaviour;
  plane: 0 | 1; // 0 = far (behind barn front and fence), 1 = near (in front of the fence)
  x: number;
  y: number;
  flip: boolean;
  clip: number | null;
  state: TState;
  st: number;
  anim: number;
  blinkAt: number;
  phase: string;
  pt: number;
  pdur: number;
  vx: number;
  baseY: number;
  targetBaseY: number;
  amp: number;
  w: number;
  from: Point;
  to: Point;
  dist: number;
  hidden: boolean;
  visible: number;
  topX: number;
  topY: number;
  badgeVisible: boolean;
  /** Seconds this turkey has been in view since it last appeared. */
  shown: number;
}

interface Particle { x: number; y: number; vx: number; vy: number; life: number; max: number; col: string; kind: "feather" | "dust" | "leaf" | "spark" }
interface Ring { x: number; y: number; t: number }

const rnd = (a: number, b: number) => a + Math.random() * (b - a);
const pick = <T,>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];
const ease = (t: number) => (t < 0 ? 0 : t > 1 ? 1 : t * t * (3 - 2 * t));

const POOLS: Behaviour[][] = [
  ["fly", "run", "hay"],
  ["fly", "run", "hay", "barn", "tree"],
  ["fly", "run", "hay", "barn", "tree", "fence"],
];

const FEATHER_COLS = ["#f1d9a6", "#a85a2c", "#6b3a1e", "#d4883e", "#ffffff"];

export interface ShotResult {
  hit: number | null;
  x: number;
  y: number;
}

export class TurkeyEngine {
  private ctx: CanvasRenderingContext2D;
  private sc: Scenery;
  private fr: TurkeyFrames;
  private raf = 0;
  private last = 0;
  private time = 0;
  private turkeys: Turkey[] = [];
  private particles: Particle[] = [];
  private rings: Ring[] = [];
  private flashes: { x: number; y: number; t: number }[] = [];
  private shake = 0;
  private speed = 1;
  private exposure = 2.2;
  private showBadges = true;
  private reduced = false;
  private leafTimer = 1;
  private clouds: { x: number; y: number; i: number; s: number }[];
  /** Called when a turkey pops into view (turkey number). */
  onAppear: ((n: number) => void) | null = null;
  /** A turkey stays in view at most this long, then retreats. */
  maxShowS = 5;

  constructor(canvas: HTMLCanvasElement) {
    canvas.width = W;
    canvas.height = H;
    this.ctx = canvas.getContext("2d")!;
    this.ctx.imageSmoothingEnabled = false;
    this.sc = getScenery();
    this.fr = getTurkeyFrames();
    this.clouds = [
      { x: 30, y: 18, i: 0, s: 1 },
      { x: 170, y: 40, i: 1, s: 0.7 },
      { x: 300, y: 12, i: 2, s: 1.2 },
      { x: 400, y: 52, i: 3, s: 0.85 },
    ];
  }

  start() {
    if (this.raf) return;
    this.last = performance.now();
    const loop = (now: number) => {
      const dt = Math.min(0.05, Math.max(0, (now - this.last) / 1000));
      this.last = now;
      this.update(dt);
      this.draw();
      this.raf = requestAnimationFrame(loop);
    };
    this.raf = requestAnimationFrame(loop);
  }

  stop() {
    cancelAnimationFrame(this.raf);
    this.raf = 0;
  }

  setReducedMotion(on: boolean) {
    this.reduced = on;
  }

  /** Empty the field (ready screen). */
  clear() {
    this.turkeys = [];
  }

  /** Pick three distinct behaviours for a round. `difficulty` is 0..1. */
  setupRound(difficulty: number, opts: { badges?: boolean } = {}) {
    const d = Math.max(0, Math.min(1, difficulty));
    this.speed = 1 + 0.6 * d;
    this.exposure = 2.4 - d;
    this.showBadges = opts.badges !== false;
    const pool = [...POOLS[d < 0.25 ? 0 : d < 0.6 ? 1 : 2]];
    const chosen: Behaviour[] = [];
    while (chosen.length < 3) {
      const b = pool.splice(Math.floor(Math.random() * pool.length), 1)[0];
      chosen.push(b);
    }
    // Shuffle which number gets which behaviour.
    chosen.sort(() => Math.random() - 0.5);
    this.turkeys = chosen.map((b, i) => this.makeTurkey(i + 1, b));
  }

  private makeTurkey(n: number, b: Behaviour): Turkey {
    const t: Turkey = {
      n, b, plane: b === "fly" || b === "barn" || b === "fence" ? 0 : 1,
      x: -60, y: 200, flip: false, clip: null, state: "active", st: 0, anim: Math.random() * 3,
      blinkAt: rnd(1, 3), phase: "away", pt: 0, pdur: rnd(0.1, 0.8), vx: 0, baseY: 100, targetBaseY: 100,
      amp: 6, w: 2, from: { x: 0, y: 0 }, to: { x: 0, y: 0 }, dist: 0, hidden: true,
      visible: 0, topX: 0, topY: 0, badgeVisible: false, shown: 0,
    };
    if (b === "barn" || b === "tree" || b === "hay") {
      this.pickHideSpot(t);
      t.x = t.from.x;
      t.y = t.from.y;
      t.phase = "hidden";
    } else if (b === "fence") {
      t.clip = GEO.fence.board + 4;
      t.x = rnd(110, 320);
      t.y = 220;
      t.phase = "under";
    }
    return t;
  }

  private pickHideSpot(t: Turkey) {
    if (t.b === "barn") {
      const loft = Math.random() < 0.35;
      const fromRight = Math.random() < 0.5;
      if (loft) {
        t.from = { x: fromRight ? 420 : 372, y: 128 };
        t.to = { x: fromRight ? 402 : 390, y: 128 };
      } else {
        t.from = { x: fromRight ? 430 : 362, y: GEO.barn.bottom };
        t.to = { x: 396, y: GEO.barn.bottom };
      }
      t.flip = fromRight;
      t.clip = null;
    } else if (t.b === "tree") {
      const left = Math.random() < 0.3;
      t.from = { x: 48, y: 212 };
      t.to = left ? { x: 24, y: 212 } : { x: Math.random() < 0.5 ? 70 : 88, y: 212 };
      t.flip = left;
      t.clip = null;
    } else {
      const bale = GEO.hay[Math.floor(Math.random() * GEO.hay.length)];
      const cx = bale.x + bale.w / 2 + rnd(-6, 6);
      t.from = { x: cx, y: bale.y + bale.h + 32 };
      t.to = { x: cx, y: bale.y + 13 };
      t.flip = Math.random() < 0.5;
      t.clip = bale.y + bale.h - 1;
    }
  }

  /** Release the turkeys. */
  beginRound() {
    for (const t of this.turkeys) {
      t.state = "active";
      t.pt = 0;
    }
  }

  get hasTurkeys() {
    return this.turkeys.some((t) => t.state !== "gone");
  }

  /** Approximate body centre of turkey `n` in internal pixels. */
  turkeyPos(n: number): { x: number; y: number } | null {
    const t = this.turkeys.find((k) => k.n === n);
    return t ? { x: t.x, y: t.y - 14 } : null;
  }

  // ---------------------------------------------------------------- update

  private update(dt: number) {
    this.time += dt;
    const wind = 0.6 + 0.4 * Math.sin(this.time * 0.13);
    for (const c of this.clouds) {
      c.x += (3 + wind * 4) * c.s * dt;
      if (c.x > W + 10) { c.x = -70; c.y = rnd(8, 60); }
    }
    for (const t of this.turkeys) this.updateTurkey(t, dt);
    this.turkeys = this.turkeys.filter((t) => t.state !== "gone");
    for (const t of this.turkeys) this.measure(t);

    // Particles.
    for (const p of this.particles) {
      p.life -= dt;
      if (p.kind === "feather" || p.kind === "leaf") {
        p.vy = Math.min(p.vy + 22 * dt, 18);
        p.vx = p.vx * (1 - 1.5 * dt) + Math.sin(this.time * 3 + p.y * 0.2) * 10 * dt + wind * 3 * dt;
      } else if (p.kind === "spark") {
        p.vy += 120 * dt;
      } else {
        p.vx *= 1 - 3 * dt;
        p.vy *= 1 - 3 * dt;
      }
      p.x += p.vx * dt;
      p.y += p.vy * dt;
    }
    this.particles = this.particles.filter((p) => p.life > 0 && p.y < H + 8);
    for (const r of this.rings) r.t += dt;
    this.rings = this.rings.filter((r) => r.t < 0.28);
    for (const f of this.flashes) f.t += dt;
    this.flashes = this.flashes.filter((f) => f.t < 0.07);
    this.shake = Math.max(0, this.shake - dt);

    if (!this.reduced) {
      this.leafTimer -= dt;
      if (this.leafTimer <= 0) {
        this.leafTimer = rnd(1.2, 3.5);
        this.particles.push({ x: rnd(10, 120), y: rnd(40, 100), vx: rnd(2, 8), vy: 2, life: 6, max: 6, col: pick(["#3d8435", "#55a243", "#c9a24a"]), kind: "leaf" });
      }
    }
  }

  private updateTurkey(t: Turkey, dt: number) {
    t.anim += dt;
    t.st += dt;
    t.blinkAt -= dt;
    if (t.blinkAt < -0.12) t.blinkAt = rnd(1.8, 4);

    if (t.state === "spin") {
      if (t.st > 0.95) {
        this.featherPuff(t.x, t.y - 12, this.reduced ? 8 : 16);
        t.state = "gone";
      }
      return;
    }
    if (t.state === "dizzy") {
      if (t.st > 1.0) this.toExit(t);
      return;
    }
    if (t.state === "exit") {
      this.updateExit(t, dt);
      return;
    }
    const wasHidden = t.hidden;
    switch (t.b) {
      case "fly": this.updateFly(t, dt); break;
      case "run": this.updateRun(t, dt); break;
      case "fence": this.updateFence(t, dt); break;
      default: this.updateHide(t, dt);
    }
    if (t.hidden) {
      t.shown = 0;
      return;
    }
    if (wasHidden) this.onAppear?.(t.n);
    t.shown += dt;
    if (t.shown > this.maxShowS) this.retreat(t);
  }

  /** Time is up for this appearance: the turkey leaves view, then comes back later. */
  private retreat(t: Turkey) {
    t.shown = 0;
    if (t.b === "fly" || t.b === "run") {
      this.particles.push({ x: t.x, y: t.y - 10, vx: 0, vy: -6, life: 0.45, max: 0.45, col: "#efe4c8", kind: "dust" });
      t.phase = "away";
      t.pt = 0;
      t.pdur = rnd(0.4, 1.0);
      t.hidden = true;
    } else if (t.b === "fence") {
      if (t.phase !== "duck" && t.phase !== "under") {
        t.phase = "duck";
        t.pt = 0;
      }
    } else if (t.phase !== "in" && t.phase !== "hidden") {
      t.phase = "in";
      t.pt = 0;
    }
  }

  private updateFly(t: Turkey, dt: number) {
    t.pt += dt;
    if (t.phase === "away") {
      t.hidden = true;
      if (t.pt < t.pdur) return;
      const fromLeft = Math.random() < 0.5;
      t.x = fromLeft ? -18 : W + 18;
      t.vx = (fromLeft ? 1 : -1) * rnd(38, 55) * this.speed;
      t.flip = !fromLeft;
      t.baseY = rnd(86, 126);
      t.targetBaseY = rnd(86, 126);
      t.amp = rnd(4, 12);
      t.w = rnd(1.6, 2.6);
      t.phase = "fly";
      t.pt = 0;
      t.hidden = false;
    }
    t.baseY += (t.targetBaseY - t.baseY) * Math.min(1, dt * 0.6);
    if (Math.abs(t.targetBaseY - t.baseY) < 2) t.targetBaseY = rnd(86, 126);
    t.x += t.vx * dt;
    t.y = t.baseY + t.amp * Math.sin(t.w * t.pt);
    if ((t.vx > 0 && t.x > W + 22) || (t.vx < 0 && t.x < -22)) {
      t.phase = "away";
      t.pt = 0;
      t.pdur = rnd(0.4, 1.0);
    }
  }

  private updateRun(t: Turkey, dt: number) {
    t.pt += dt;
    if (t.phase === "away") {
      t.hidden = true;
      if (t.pt < t.pdur) return;
      const fromLeft = Math.random() < 0.5;
      t.x = fromLeft ? -18 : W + 18;
      t.y = rnd(202, 211);
      t.vx = (fromLeft ? 1 : -1) * rnd(42, 60) * this.speed;
      t.flip = !fromLeft;
      t.dist = rnd(60, 140);
      t.phase = "walk";
      t.pt = 0;
      t.hidden = false;
    }
    if (t.phase === "walk") {
      t.x += t.vx * dt;
      t.dist -= Math.abs(t.vx * dt);
      if (t.dist <= 0 && t.x > 20 && t.x < W - 20) {
        t.phase = "stop";
        t.pt = 0;
        t.pdur = rnd(0.35, 0.9) / Math.sqrt(this.speed);
      }
      if ((t.vx > 0 && t.x > W + 22) || (t.vx < 0 && t.x < -22)) {
        t.phase = "away";
        t.pt = 0;
        t.pdur = rnd(0.4, 1.0);
      }
    } else if (t.phase === "stop" && t.pt > t.pdur) {
      if (Math.random() < 0.3) {
        t.vx = -t.vx;
        t.flip = t.vx < 0;
      }
      t.dist = rnd(60, 140);
      t.phase = "walk";
      t.pt = 0;
    }
  }

  private updateFence(t: Turkey, dt: number) {
    t.pt += dt;
    const F = GEO.fence;
    const top = 190;
    const low = 220;
    if (t.phase === "under") {
      t.hidden = true;
      t.y = low;
      if (t.pt > t.pdur) {
        t.x = rnd(110, 320);
        t.flip = Math.random() < 0.5;
        t.phase = "rise";
        t.pt = 0;
      }
    } else if (t.phase === "rise") {
      t.hidden = false;
      const k = ease(t.pt / (0.3 / this.speed));
      t.y = low + (top - low) * k;
      if (k >= 1) this.fenceWalk(t);
    } else if (t.phase === "walk") {
      const dir = Math.sign(t.to.x - t.x);
      t.x += dir * 30 * this.speed * dt;
      t.flip = dir < 0;
      if ((dir > 0 && t.x >= t.to.x) || (dir < 0 && t.x <= t.to.x) || dir === 0) {
        t.phase = "stop";
        t.pt = 0;
        t.pdur = rnd(0.4, 0.9);
      }
    } else if (t.phase === "stop") {
      if (t.pt > t.pdur) {
        if (Math.random() < 0.5) {
          t.phase = "duck";
          t.pt = 0;
        } else this.fenceWalk(t);
      }
    } else if (t.phase === "duck") {
      const k = ease(t.pt / (0.28 / this.speed));
      t.y = top + (low - top) * k;
      if (k >= 1) {
        t.phase = "under";
        t.pt = 0;
        t.pdur = rnd(0.5, 1.0);
      }
    }
    void F;
  }

  private fenceWalk(t: Turkey) {
    t.to = { x: Math.max(108, Math.min(326, t.x + (Math.random() < 0.5 ? -1 : 1) * rnd(40, 110))), y: 190 };
    t.phase = "walk";
    t.pt = 0;
  }

  private updateHide(t: Turkey, dt: number) {
    t.pt += dt;
    const tween = 0.3 / this.speed;
    if (t.phase === "hidden") {
      t.hidden = true;
      t.x = t.from.x;
      t.y = t.from.y;
      if (t.pt > t.pdur) {
        t.phase = "out";
        t.pt = 0;
      }
    } else if (t.phase === "out") {
      t.hidden = false;
      const k = ease(t.pt / tween);
      t.x = t.from.x + (t.to.x - t.from.x) * k;
      t.y = t.from.y + (t.to.y - t.from.y) * k;
      if (k >= 1) {
        t.phase = "show";
        t.pt = 0;
        t.pdur = this.exposure * rnd(0.85, 1.15);
      }
    } else if (t.phase === "show") {
      if (t.pt > t.pdur) {
        t.phase = "in";
        t.pt = 0;
      }
    } else if (t.phase === "in") {
      const k = ease(t.pt / tween);
      t.x = t.to.x + (t.from.x - t.to.x) * k;
      t.y = t.to.y + (t.from.y - t.to.y) * k;
      if (k >= 1) {
        this.pickHideSpot(t);
        t.x = t.from.x;
        t.y = t.from.y;
        t.phase = "hidden";
        t.pt = 0;
        t.pdur = rnd(0.45, 0.9);
      }
    }
  }

  private toExit(t: Turkey) {
    t.state = "exit";
    t.st = 0;
    t.pt = 0;
    if (t.b === "fly" || t.b === "run") {
      if (t.hidden) { t.state = "gone"; return; }
      const goRight = t.b === "run" ? t.x > W / 2 : t.vx >= 0;
      t.vx = (goRight ? 1 : -1) * 150;
      t.flip = !goRight;
    } else if (t.b === "fence") {
      t.phase = "duck";
    } else {
      t.phase = "in";
      if (t.hidden) t.state = "gone";
    }
  }

  private updateExit(t: Turkey, dt: number) {
    t.pt += dt;
    if (t.b === "fly") {
      t.x += t.vx * dt;
      t.y -= 30 * dt;
      if (t.x < -30 || t.x > W + 30) t.state = "gone";
    } else if (t.b === "run") {
      t.x += t.vx * dt;
      if (t.x < -30 || t.x > W + 30) t.state = "gone";
    } else if (t.b === "fence") {
      const k = ease(t.pt / 0.3);
      t.y = t.y + (222 - t.y) * k;
      if (k >= 1) t.state = "gone";
    } else {
      const k = ease(t.pt / 0.35);
      t.x = t.x + (t.from.x - t.x) * k;
      t.y = t.y + (t.from.y - t.y) * k;
      if (k >= 1) t.state = "gone";
    }
  }

  // ------------------------------------------------------------- resolve

  /** Animate the outcome. `hitN` is the shot turkey (null on timeout). */
  resolve(hitN: number | null, correct: boolean) {
    for (const t of this.turkeys) {
      t.st = 0;
      if (t.n === hitN) {
        if (correct) {
          t.state = "spin";
          this.featherPuff(t.x, t.y - 12, this.reduced ? 6 : 12);
        } else {
          t.state = "dizzy";
        }
      } else {
        this.toExit(t);
      }
    }
    if (hitN !== null && !this.reduced) this.shake = 0.14;
  }

  // ---------------------------------------------------------------- input

  /** Shoot at internal coordinates. Effects are added here. */
  shoot(x: number, y: number, touch = false): ShotResult {
    const ix = Math.round(x);
    const iy = Math.round(y);
    this.rings.push({ x: ix, y: iy, t: 0 });
    this.flashes.push({ x: ix, y: iy, t: 0 });
    const hit = this.hitTest(ix, iy, touch ? 3 : 1);
    if (hit === null) {
      for (let i = 0; i < (this.reduced ? 3 : 6); i++)
        this.particles.push({ x: ix, y: iy, vx: rnd(-30, 30), vy: rnd(-50, -10), life: 0.35, max: 0.35, col: pick(["#fff3b0", "#ffd23f", "#ffffff"]), kind: "spark" });
      this.particles.push({ x: ix, y: iy, vx: 0, vy: -4, life: 0.4, max: 0.4, col: "#efe4c8", kind: "dust" });
    }
    return { hit, x: ix, y: iy };
  }

  private spriteFor(t: Turkey): TurkeySprite {
    if (t.state === "dizzy") return this.fr.dizzy;
    if (t.state === "spin") return this.fr.stand;
    if (t.b === "fly") return this.fr.fly[Math.floor(t.anim * 10) % 4];
    const moving =
      (t.b === "run" && (t.phase === "walk" || t.state === "exit")) ||
      (t.b === "fence" && t.phase === "walk") ||
      (t.b === "tree" && (t.phase === "out" || t.phase === "in"));
    if (moving) return this.fr.run[Math.floor(t.anim * (t.state === "exit" ? 18 : 12)) % 4];
    if (t.phase === "stop" && t.b === "run" && Math.floor(t.pt * 3) % 2 === 1) return this.fr.peck;
    if (t.blinkAt < 0) return this.fr.blink;
    return this.fr.stand;
  }

  /** Sprite-local pixel for a world pixel, or -1 when outside. */
  private localIndex(t: Turkey, X: number, Y: number): number {
    const tx = Math.round(t.x);
    const ty = Math.round(t.y);
    const c = t.flip ? tx + TK_AX - 1 - X : X - tx + TK_AX;
    const r = Y - ty + TK_AY;
    if (c < 0 || c >= TK_W || r < 0 || r >= TK_H) return -1;
    return r * TK_W + c;
  }

  private pixelVisible(t: Turkey, sprite: TurkeySprite, X: number, Y: number): boolean {
    if (X < 0 || X >= W || Y < 0 || Y >= H) return false;
    if (t.clip !== null && Y >= t.clip) return false;
    const li = this.localIndex(t, X, Y);
    if (li < 0 || !sprite.alpha[li]) return false;
    const mask = t.plane === 0 ? this.sc.maskA : this.sc.maskB;
    return !mask[Y * W + X];
  }

  private hitTest(x: number, y: number, r: number): number | null {
    const order = [...this.turkeys].filter((t) => t.state === "active" && !t.hidden).sort((a, b) => b.plane - a.plane);
    for (const t of order) {
      const sprite = this.spriteFor(t);
      for (let dy = -r; dy <= r; dy++)
        for (let dx = -r; dx <= r; dx++) if (this.pixelVisible(t, sprite, x + dx, y + dy)) return t.n;
    }
    return null;
  }

  private measure(t: Turkey) {
    t.visible = 0;
    t.topY = 9999;
    t.badgeVisible = false;
    if (t.hidden && t.state === "active") return;
    const sprite = this.spriteFor(t);
    const tx = Math.round(t.x);
    const ty = Math.round(t.y);
    for (let r = 0; r < TK_H; r++) {
      const Y = ty - TK_AY + r;
      for (let c = 0; c < TK_W; c++) {
        if (!sprite.alpha[r * TK_W + c]) continue;
        const X = t.flip ? tx + TK_AX - 1 - c : tx - TK_AX + c;
        if (!this.pixelVisible(t, sprite, X, Y)) continue;
        t.visible++;
        if (Y < t.topY) { t.topY = Y; t.topX = X; }
      }
    }
    const bx = tx + (t.flip ? -TK_BADGE[0] : TK_BADGE[0]);
    const by = ty + TK_BADGE[1];
    t.badgeVisible = this.pixelVisible(t, sprite, bx, by) && this.pixelVisible(t, sprite, bx, by - 2) && this.pixelVisible(t, sprite, bx, by + 2);
  }

  private featherPuff(x: number, y: number, count: number) {
    for (let i = 0; i < count; i++) {
      const a = rnd(0, Math.PI * 2);
      const s = rnd(20, 60);
      this.particles.push({ x, y, vx: Math.cos(a) * s, vy: Math.sin(a) * s - 20, life: rnd(0.8, 1.6), max: 1.6, col: pick(FEATHER_COLS), kind: "feather" });
    }
  }

  // ------------------------------------------------------------------ draw

  private draw() {
    const ctx = this.ctx;
    const sc = this.sc;
    ctx.save();
    if (this.shake > 0) ctx.translate(Math.round(rnd(-2, 2)), Math.round(rnd(-2, 2)));
    ctx.drawImage(sc.bg, 0, 0);
    for (const c of this.clouds) ctx.drawImage(sc.clouds[c.i], Math.round(c.x), Math.round(c.y));
    this.drawLake();
    this.drawWindmill();
    for (const t of this.turkeys) if (t.plane === 0) this.drawTurkey(t);
    ctx.drawImage(sc.barnFront, 0, 0);
    ctx.drawImage(sc.fence, 0, 0);
    for (const t of this.turkeys) if (t.plane === 1) this.drawTurkey(t);
    ctx.drawImage(sc.tree, 0, 0);
    ctx.drawImage(sc.hay, 0, 0);
    ctx.drawImage(sc.fg, 0, 0);
    this.drawGrass();
    this.drawParticles();
    this.drawEffects();
    if (this.showBadges) for (const t of this.turkeys) this.drawBadge(t);
    for (const t of this.turkeys) if (t.state === "dizzy") this.drawStars(t);
    ctx.restore();
  }

  private drawLake() {
    const L = GEO.lake;
    for (let i = 0; i < 12; i++) {
      const x = L.x0 + 14 + ((i * 53) % (L.x1 - L.x0 - 28));
      const y = L.y0 + 4 + ((i * 7) % 9);
      if (Math.sin(this.time * 2.2 + i * 1.7) > 0.55) rect(this.ctx, x, y, 3, 1, "#d6eefc");
    }
  }

  private drawWindmill() {
    const [hx, hy] = GEO.windmillHub;
    const a0 = this.reduced ? 0.4 : this.time * 0.9;
    for (let b = 0; b < 4; b++) {
      const a = a0 + (b * Math.PI) / 2;
      const ca = Math.cos(a);
      const sa = Math.sin(a);
      for (let r = 2; r < 19; r++) {
        px(this.ctx, hx + ca * r, hy + sa * r, "#5b4a3a");
        if (r > 6) {
          for (let w = 1; w <= 3; w++) px(this.ctx, hx + ca * r - sa * w, hy + sa * r + ca * w, r % 4 === 0 ? "#cfc4ae" : "#efe7d4");
        }
      }
    }
    ellipse(this.ctx, hx, hy, 2, 2, "#3f3226");
  }

  private drawTurkey(t: Turkey) {
    if (t.hidden && t.state === "active") return;
    const ctx = this.ctx;
    const sprite = this.spriteFor(t);
    const tx = Math.round(t.x);
    let ty = Math.round(t.y);
    let sx = t.flip ? -1 : 1;
    if (t.state === "spin") {
      sx *= Math.cos(t.st * 14);
      if (Math.abs(sx) < 0.15) sx = sx < 0 ? -0.15 : 0.15;
      ty -= Math.round(Math.sin(Math.min(1, t.st / 0.95) * Math.PI) * 14 + t.st * 6);
    }
    ctx.save();
    if (t.clip !== null) {
      ctx.beginPath();
      ctx.rect(0, 0, W, t.clip);
      ctx.clip();
    }
    ctx.translate(tx, ty);
    ctx.scale(sx, 1);
    if (t.state === "dizzy") ctx.rotate(Math.sin(t.st * 18) * 0.08);
    ctx.drawImage(sprite.canvas, -TK_AX, -TK_AY);
    ctx.restore();
  }

  private drawBadge(t: Turkey) {
    if (t.state === "spin" || t.state === "gone") return;
    if (t.hidden && t.state === "active") return;
    let bx: number;
    let by: number;
    let floating = false;
    if (t.badgeVisible) {
      bx = Math.round(t.x) + (t.flip ? -TK_BADGE[0] : TK_BADGE[0]);
      by = Math.round(t.y) + TK_BADGE[1];
    } else if (t.visible > 0) {
      bx = t.topX;
      by = t.topY - 8;
      floating = true;
    } else return;
    bx = Math.max(7, Math.min(W - 7, bx));
    by = Math.max(60, Math.min(H - 7, by));
    const ctx = this.ctx;
    if (floating) {
      rect(ctx, bx, by + 5, 1, 2, "#2a1a0a");
    }
    ellipse(ctx, bx + 0.5, by + 0.5, 5.6, 5.6, "#2a1a0a");
    ellipse(ctx, bx + 0.5, by + 0.5, 4.6, 4.6, "#ffd23f");
    px(ctx, bx - 2, by - 2, "#fff1a6");
    digit(ctx, String(t.n), bx - 1, by - 2, "#2a1a0a");
  }

  private drawStars(t: Turkey) {
    const hx = Math.round(t.x) + (t.flip ? -6 : 6);
    const hy = Math.round(t.y) - 29;
    for (let i = 0; i < 3; i++) {
      const a = t.st * 6 + (i * Math.PI * 2) / 3;
      const x = hx + Math.cos(a) * 7;
      const y = hy + Math.sin(a) * 2.5;
      px(this.ctx, x, y, "#fff3b0");
      px(this.ctx, x - 1, y, "#ffd23f");
      px(this.ctx, x + 1, y, "#ffd23f");
      px(this.ctx, x, y - 1, "#ffd23f");
      px(this.ctx, x, y + 1, "#ffd23f");
    }
  }

  private drawGrass() {
    const ctx = this.ctx;
    const wind = 0.6 + 0.4 * Math.sin(this.time * 0.13);
    for (let i = 0; i < 44; i++) {
      const x = 4 + i * 11 + ((i * 7) % 5);
      const base = 236 + ((i * 13) % 10);
      const h = 8 + ((i * 5) % 7);
      const sway = this.reduced ? 1 : Math.sin(this.time * 1.6 + i * 0.7) * 2 * wind + wind;
      for (let k = 0; k < h; k++) {
        const f = k / h;
        px(ctx, x + sway * f * f, base - k, k > h - 3 ? "#7ccf5c" : "#4f9a3c");
      }
    }
  }

  private drawParticles() {
    const ctx = this.ctx;
    for (const p of this.particles) {
      if (p.kind === "dust") {
        const k = 1 - p.life / p.max;
        ctx.globalAlpha = 1 - k;
        ellipse(ctx, p.x, p.y, 2 + k * 5, 1.5 + k * 3, p.col);
        ctx.globalAlpha = 1;
      } else if (p.kind === "spark") {
        px(ctx, p.x, p.y, p.col);
      } else {
        const flipX = Math.sin(this.time * 6 + p.x) > 0;
        if (flipX) rect(ctx, p.x, p.y, 2, 1, p.col);
        else rect(ctx, p.x, p.y, 1, 2, p.col);
      }
    }
  }

  private drawEffects() {
    const ctx = this.ctx;
    for (const r of this.rings) {
      const k = r.t / 0.28;
      const rad = 2 + k * 9;
      const col = k < 0.5 ? "#ffffff" : "#ffd23f";
      const steps = Math.ceil(rad * 6);
      for (let i = 0; i < steps; i++) {
        const a = (i / steps) * Math.PI * 2;
        px(ctx, r.x + Math.cos(a) * rad, r.y + Math.sin(a) * rad, col);
      }
    }
    for (const f of this.flashes) {
      rect(ctx, f.x - 1, f.y - 1, 3, 3, "#ffffff");
      rect(ctx, f.x - 4, f.y, 9, 1, "#fff3b0");
      rect(ctx, f.x, f.y - 4, 1, 9, "#fff3b0");
    }
  }
}
